import { toast as sonnerToast } from "sonner";
import type React from "react";
import { isValidElement, type ReactNode } from "react";

type ToastId = string | number;
type ToastInput = string | CompatToastOptions;
type ToastPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right" | "top-center" | "bottom-center";

interface CompatToastOptions {
  className?: string;
  id?: ToastId;
  title?: string;
  description?: ReactNode | string;
  status?: "success" | "error" | "info" | "warning" | "loading";
  type?: "success" | "error" | "info" | "warning" | "loading";
  duration?: number | null;
  icon?: ReactNode;
  isClosable?: boolean;
  position?: ToastPosition;
  style?: React.CSSProperties;
}

interface CompatPromiseOptions<T> {
  loading?: string;
  success?: string | ((data: T) => string);
  error?: string | ((error: Error) => string);
}

type ToastFn = ((input: ToastInput, options?: CompatToastOptions) => string) & {
  success: (input: ToastInput, options?: CompatToastOptions) => string;
  error: (input: ToastInput, options?: CompatToastOptions) => string;
  info: (input: ToastInput, options?: CompatToastOptions) => string;
  warning: (input: ToastInput, options?: CompatToastOptions) => string;
  dismiss: (id?: ToastId) => void;
  clear: () => void;
  promise: <T>(promise: Promise<T>, options?: CompatPromiseOptions<T>) => Promise<T>;
  custom: (renderer: (id: string) => ReactNode, options?: CompatToastOptions) => string;
};

function isCompatToastOptions(input: ToastInput): input is CompatToastOptions {
  return typeof input === "object" && input !== null && !Array.isArray(input) && !isValidElement(input);
}

function normalizeOptions(
  input: ToastInput,
  options?: CompatToastOptions,
): { title: string | undefined; rest: Omit<CompatToastOptions, "title" | "status" | "type"> } {
  const merged = isCompatToastOptions(input)
    ? { ...input, ...options }
    : { ...options, title: typeof input === "string" ? input : options?.title };
  const { title, status, type, ...rest } = merged;
  void status;
  void type;
  return { title, rest };
}

function emitToast(
  input: ToastInput,
  method: typeof sonnerToast | typeof sonnerToast.success,
  options?: CompatToastOptions,
): string {
  const { title, rest } = normalizeOptions(input, options);
  const id = method(title ?? "", {
    id: rest.id != null ? String(rest.id) : undefined,
    description: rest.description,
    duration: rest.duration === null ? Infinity : rest.duration,
    icon: rest.icon as React.ReactElement | undefined,
    position: rest.position,
    className: rest.className,
    style: rest.style,
  });
  return String(id);
}

const toastImpl = ((input: ToastInput, options?: CompatToastOptions) =>
  emitToast(input, sonnerToast, options)) as ToastFn;

toastImpl.success = (input, options) => emitToast(input, sonnerToast.success, options);
toastImpl.error = (input, options) => emitToast(input, sonnerToast.error, options);
toastImpl.info = (input, options) => emitToast(input, sonnerToast.info, options);
toastImpl.warning = (input, options) => emitToast(input, sonnerToast.warning, options);

toastImpl.dismiss = id => {
  if (id == null) {
    sonnerToast.dismiss();
    return;
  }
  sonnerToast.dismiss(id);
};

toastImpl.clear = () => {
  sonnerToast.dismiss();
};

toastImpl.promise = async <T>(promise: Promise<T>, options?: CompatPromiseOptions<T>) => {
  return sonnerToast.promise(promise, {
    loading: options?.loading ?? "Loading...",
    success:
      typeof options?.success === "function"
        ? (data: T) => (options.success as (data: T) => string)(data)
        : (options?.success ?? "Success!"),
    error:
      typeof options?.error === "function"
        ? (err: Error) => (options.error as (error: Error) => string)(err)
        : (options?.error ?? "Something went wrong"),
  }) as unknown as Promise<T>;
};

toastImpl.custom = (renderer, options) => {
  const externalId = options?.id != null ? String(options.id) : `toast-${Math.random().toString(36).slice(2)}`;
  sonnerToast.custom(() => renderer(externalId) as React.ReactElement, {
    id: externalId,
    duration: options?.duration === null ? Infinity : options?.duration,
  });
  return externalId;
};

export const toast = toastImpl;

export const showToast = {
  success: (message: string) => toast.success(message),
  error: (message: string) => toast.error(message),
  info: (message: string) => toast.info(message),
  warning: (message: string) => toast.warning(message),
  promise: async <T>(promise: Promise<T>, options?: CompatPromiseOptions<T>) => toast.promise(promise, options),
};

export default toast;
